/* eslint-disable react-refresh/only-export-components */

import {
  createContext,
  ReactNode,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { Incident } from "@/types/incidents";
import { useIncidents } from "./IncidentsContext";

interface GroupContextType {
  groupedAlerts: Record<string, Incident[]>;
  groupStats: Record<
    string,
    {
      nearDangerCount: number;
      dangerCount: number;
      droneCount: number;
      totalAlerts: number;
      latestAlert: Incident;
    }
  >;
  isGroupAlert: (alertId: number) => boolean;
  getGroupId: (alertId: number) => string | null;
  getGroupAlerts: (groupId: string) => Incident[];
}

const GroupContext = createContext<GroupContextType | undefined>(undefined);

export function GroupProvider({ children }: Readonly<{ children: ReactNode }>) {
  const { incidents } = useIncidents();

  const groupedAlerts = useMemo(() => {
    return incidents.reduce(
      (acc, alert) => {
        if (alert.grouped && alert.groupID) {
          if (!acc[alert.groupID]) {
            acc[alert.groupID] = [];
          }
          acc[alert.groupID].push(alert);
        }
        return acc;
      },
      {} as Record<string, Incident[]>
    );
  }, [incidents]);

  const groupStats = useMemo(() => {
    return Object.entries(groupedAlerts).reduce(
      (acc, [groupId, alerts]) => {
        acc[groupId] = {
          nearDangerCount: alerts.filter(
            (a) => a.incidentType === "NEAR_DANGER"
          ).length,
          dangerCount: alerts.filter((a) => a.incidentType === "DANGER").length,
          droneCount: alerts.filter((a) => a.incidentType === "DRONE_ALERT").length,
          totalAlerts: alerts.length,
          latestAlert: alerts.reduce(
            (latest, current) =>
              new Date(current.reportedOn) > new Date(latest.reportedOn)
                ? current
                : latest,
            alerts[0]
          ),
        };
        return acc;
      },
      {} as Record<
        string,
        {
          nearDangerCount: number;
          dangerCount: number;
          droneCount: number;
          totalAlerts: number;
          latestAlert: Incident;
        }
      >
    );
  }, [groupedAlerts]);

  const isGroupAlert = useCallback(
    (alertId: number) => {
      return incidents.some(
        (inc) => inc.incidentID === alertId && inc.grouped && inc.groupID
      );
    },
    [incidents]
  );

  const getGroupId = useCallback(
    (alertId: number) => {
      const alert = incidents.find((inc) => inc.incidentID === alertId);
      return alert?.grouped && alert?.groupID ? alert.groupID : null;
    },
    [incidents]
  );

  const getGroupAlerts = useCallback(
    (groupId: string) => {
      return groupedAlerts[groupId] || [];
    },
    [groupedAlerts]
  );

  const value = useMemo(() => ({
    groupedAlerts,
    groupStats,
    isGroupAlert,
    getGroupId,
    getGroupAlerts,
  }), [groupedAlerts, groupStats, isGroupAlert, getGroupId, getGroupAlerts]);

  return (
    <GroupContext.Provider value={value}>{children}</GroupContext.Provider>
  );
}

export function useGroup() {
  const context = useContext(GroupContext);
  if (context === undefined) {
    throw new Error("useGroup must be used within a GroupProvider");
  }
  return context;
}
